import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Shield, Cpu, Layers } from 'lucide-react';
import Typewriter from 'typewriter-effect';

const Hero = () => {
  return (
    <section className="relative pt-32 pb-24 min-h-screen flex items-center justify-center overflow-hidden bg-transparent">

      {/* Dynamic Aurora & Structural Grid Background Effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Architect Grid masked with radial gradient */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,#000_70%,transparent_110%)]"></div>
        
        {/* Animated Aurora Orbs */}
        <motion.div 
          animate={{ x: [0, 80, 0], y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-gradient-to-br from-blue-400/20 to-purple-400/20 blur-[100px] mix-blend-multiply"
        />
        <motion.div 
          animate={{ x: [0, -80, 0], y: [0, 50, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[10%] right-[5%] w-[50%] h-[50%] rounded-full bg-gradient-to-bl from-indigo-400/20 to-cyan-400/20 blur-[120px] mix-blend-multiply"
        />
        <motion.div 
          animate={{ x: [0, 50, 0], y: [0, 80, 0], scale: [1.1, 1, 1.1] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-[-10%] left-[20%] w-[60%] h-[60%] rounded-full bg-gradient-to-tr from-blue-500/10 to-transparent blur-[100px] mix-blend-multiply"
        />
      </div>

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Content Area (Text & CTA) */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left mt-10 lg:mt-0">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: -20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-blue-50/80 backdrop-blur-md border border-blue-100 text-blue-700 font-semibold text-sm mb-8 shadow-sm"
            >
              <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></div>
              Solving Critical Business Problems Through Advanced Technology
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 leading-[1.15] tracking-tight mb-8"
            >
              Transform Ideas Into <br className="hidden lg:block"/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-indigo-600 to-purple-600 block min-h-[90px] mt-2">
                <Typewriter
                  options={{
                    strings: ['Digital Reality', 'Web Platforms', 'Mobile Apps', 'Cloud Solutions'],
                    autoStart: true,
                    loop: true,
                    delay: 50,
                    deleteSpeed: 30,
                  }}
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-slate-600 mb-12 max-w-2xl leading-relaxed font-normal"
            >
              We partner with ambitious companies to solve complex operations, build trust, and unlock growth. Our team of elite engineers designs customized, high-value tech systems that deliver real business outcomes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center gap-5 w-full sm:w-auto"
            >
              <button className="group w-full sm:w-auto flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-[0_10px_30px_rgb(79,70,229,0.3)] hover:shadow-[0_10px_40px_rgb(79,70,229,0.4)] hover:-translate-y-1">
                Start Your Project
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </button>
              <button className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-slate-800 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-sm">
                Discover Our Work
              </button>
            </motion.div>
          </div>

          {/* Right Content Area (Floating 3D Composition) */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="relative h-[500px] lg:h-[650px] w-full hidden md:flex items-center justify-center"
          >
             {/* Center Glow Sphere */}
             <motion.div 
               animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
               transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
               className="absolute w-[300px] h-[300px] bg-white/40 backdrop-blur-xl border border-white/60 rounded-full shadow-[0_0_80px_rgba(79,70,229,0.15)] flex items-center justify-center"
             >
               <div className="w-[200px] h-[200px] bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full blur-[60px] opacity-30"></div>
               
               {/* Core Engine representation */}
               <div className="absolute inset-0 m-auto w-32 h-32 bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100 flex items-center justify-center z-20">
                 <Cpu size={56} className="text-blue-600" />
               </div>
             </motion.div>

             {/* Floating Element 1 - Security */}
             <motion.div 
               animate={{ y: [0, 20, 0], x: [0, 10, 0] }}
               transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
               className="absolute top-[10%] left-[0%] bg-white p-5 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-slate-100 flex items-center gap-4 z-30"
             >
               <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-500 shadow-inner">
                 <Shield size={28} />
               </div>
               <div>
                  <p className="text-sm font-bold text-slate-800">Operational Growth</p>
                  <p className="text-xs text-slate-500 font-medium mt-1">Tech built for scaling</p>
               </div>
             </motion.div>

             {/* Floating Element 2 - Code/Web */}
             <motion.div 
               animate={{ y: [0, -20, 0], x: [0, -15, 0] }}
               transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
               className="absolute bottom-[10%] left-[5%] bg-white p-5 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-slate-100 flex items-center gap-4 z-30"
             >
               <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shadow-inner">
                 <Code size={28} />
               </div>
               <div>
                  <p className="text-sm font-bold text-slate-800">Business Solutions</p>
                  <p className="text-xs text-slate-500 font-medium mt-1">Tailored technology systems</p>
               </div>
             </motion.div>

             {/* Floating Element 3 - Cloud */}
             <motion.div 
               animate={{ y: [0, 25, 0], x: [0, 5, 0] }}
               transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
               className="absolute top-[35%] right-[-5%] bg-white p-5 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-slate-100 flex items-center gap-4 z-30"
             >
               <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center text-purple-600 shadow-inner">
                 <Layers size={28} />
               </div>
               <div>
                  <p className="text-sm font-bold text-slate-800">Trusted Partnership</p>
                  <p className="text-xs text-slate-500 font-medium mt-1">Aligned with your success</p>
               </div>
             </motion.div>

          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
